import { apiClient } from '@services/api-client'
import { SNA_PREFIX } from '@services/utils'
import { useOnError } from '@services/utils/onError'
import { useMutation, useQuery } from '@tanstack/react-query'

// Hand-written until the OpenAPI types are regenerated.

export type ContractDraftRecipient = {
  email: string
  name: string
  /** The mail provider took the e-mail. */
  sent: boolean
}

/** One send of a contract draft (tenant or landlord contract). */
export type ContractDraft = {
  id: string
  created_at: string
  sent_by_name: string | null
  recipients: ContractDraftRecipient[]
  params: Record<string, unknown>
  has_document: boolean
}

export type ContractDraftTarget =
  { kind: 'offer'; offerId: string } | { kind: 'flat'; flatId: string }

export const contractDraftsQueryKey = ['contract-drafts']

export function useContractDrafts(target: ContractDraftTarget) {
  const params =
    target.kind === 'offer'
      ? { offer_id: target.offerId }
      : { flat_id: target.flatId }
  return useQuery({
    queryKey: [...contractDraftsQueryKey, params],
    queryFn: async () => {
      const res = await apiClient.get<ContractDraft[]>(
        `${SNA_PREFIX}contracts/${target.kind === 'offer' ? 'offers' : 'flats'}/drafts`,
        { params }
      )
      return res.data
    }
  })
}

/** Opens the PDF exactly as it went out, in a new tab. */
export function useOpenContractDraftPdf() {
  const onError = useOnError()

  return useMutation({
    mutationFn: async (id: string) => {
      const res = await apiClient.get<Blob>(
        `${SNA_PREFIX}contracts/drafts/${id}/pdf`,
        { responseType: 'blob' }
      )
      // Errors come back as a JSON blob because of responseType.
      if (res.data.type === 'application/json') {
        throw JSON.parse(await res.data.text())
      }
      return new Blob([res.data], { type: 'application/pdf' })
    },
    onSuccess: (pdf) => {
      window.open(URL.createObjectURL(pdf), '_blank')
    },
    onError
  })
}
